import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useApp } from "../context/AppContextProvider";
import { BaseLoading } from "../library";
import { useAuth } from "../context/UserContextProvider";
// EAGER (entry chunk): the boot-critical path — landing/login, profile selection, both dashboards,
// the chrome, and every error screen (an error screen must render even when a lazy chunk itself
// fails to fetch). Everything else is route-level lazy so its chunk downloads on first navigation.
// Common
import LandingScreen from "../screens/common/landing/LandingScreen";
import ErrorScreen from "../screens/common/error/ErrorScreen";
import LoginScreen from "../screens/common/login/LoginScreen";
import NetworkErrorScreen from "../screens/common/error/NetworkErrorScreen";
import PermissionErrorScreen from "../screens/common/error/PermissionErrorScreen";
import ProfileSelectionScreen from "../screens/common/profile/ProfileSelectionScreen";
import TokenErrorScreen from "../screens/common/error/TokenErrorScreen";
import AccountActivationErrorScreen from "../screens/common/error/AccountActivationErrorScreen";
import ProfileInactiveErrorScreen from "../screens/common/error/ProfileInactiveErrorScreen";
import MainLeftNavigation from "../components/common/navigations/MainLeftNavigation";
import DuelSocialLayer from "../components/learner/duels/DuelSocialLayer";
import SessionInactiveModal from "../components/common/modals/SessionInactiveModal";
import LoginWithK12Screen from "../screens/common/login/LoginWithK12Screen";
import LoginWithSebitScreen from "../screens/common/login/LoginWithSebitScreen";
import LoginWithExternalClientScreen from "../screens/common/login/LoginWithExternalClientScreen";
import LoginWithImpersonationScreen from "../screens/common/login/LoginWithImpersonationScreen";
import DraggableMascot from "../components/common/mascots/DraggableMascot";
import ServerUnreachableErrorScreen from "../screens/common/error/ServerUnreachableErrorScreen";
import LearnerDashboardScreen from "../screens/learner/dashboards/DashboardScreen";
// LAZY — Common
const ForgotPasswordScreen = lazy(
  () => import("../screens/common/forgot-password/ForgotPasswordScreen"),
);
const SolveActivityScreen = lazy(
  () => import("../screens/common/solve/SolveActivityScreen"),
);
const FileToAnyApplicationScreen = lazy(
  () => import("../screens/common/uploads/FileToAnyApplicationScreen"),
);
const FileToAnyApplicationSuccessScreen = lazy(
  () => import("../screens/common/uploads/FileToAnyApplicationSuccessScreen"),
);
// LAZY — Learner
const LearnerBranchAssignmentScreen = lazy(
  () =>
    import("../screens/learner/branch-assignments/BranchAssignmentScreen"),
);
const LearnerBranchAssignmentReportScreen = lazy(
  () => import("../screens/learner/reports/BranchAssignmentReportScreen"),
);
const LearnerMockExamReportScreen = lazy(
  () => import("../screens/learner/reports/MockExamReportScreen"),
);
const LearnerAnnouncementListScreen = lazy(
  () => import("../screens/learner/announcements/AnnouncementListScreen"),
);
const LearnerStandardExamApplicationScreen = lazy(
  () =>
    import("../screens/learner/standard-exams/StandardExamApplicationScreen"),
);
const LearnerPractiseNowScreen = lazy(
  () => import("../screens/learner/practises/PractiseNowScreen"),
);
const LearnerPhotoSolveScreen = lazy(
  () => import("../screens/learner/photo-solve/PhotoSolveScreen"),
);
const LearnerPhotoSolutionDetailScreen = lazy(
  () => import("../screens/learner/photo-solve/PhotoSolutionDetailScreen"),
);
const PersonalizedAssignmentScreen = lazy(
  () =>
    import("../screens/learner/personalized-assignments/PersonalizedAssignmentScreen"),
);
const LearnerPractiseBranchAssignmentByIdScreen = lazy(
  () =>
    import("../screens/learner/practises/PractiseBranchAssignmentByIdScreen"),
);
const LearnerPractiseStandardExamByIdScreen = lazy(
  () =>
    import("../screens/learner/practises/PractiseStandardExamByIdScreen"),
);
const LearnerCompetitionScreen = lazy(
  () => import("../screens/learner/CompetitionScreen"),
);
const LearnerCheckContestScreen = lazy(
  () => import("../screens/learner/contests/CheckContestScreen"),
);
const LearnerContestScreen = lazy(
  () => import("../screens/learner/contests/ContestScreen"),
);
const LearnerDuelGameScreen = lazy(
  () => import("../screens/learner/duels/DuelGameScreen"),
);
const LearnerDuelSpectatorScreen = lazy(
  () => import("../screens/learner/duels/DuelSpectatorScreen"),
);
const LearnerReportListScreen = lazy(
  () => import("../screens/learner/reports/ReportListScreen"),
);
const LearnerFrameworkAnnualPlanListScreen = lazy(
  () => import("../screens/learner/plans/FrameworkAnnualPlanListScreen"),
);
const LearnerPractiseApplicationActivityByIdScreen = lazy(
  () =>
    import("../screens/learner/practises/PractiseApplicationActivityByIdScreen"),
);
const LearnerHomeworkScreen = lazy(
  () => import("../screens/learner/homeworks/HomeworkScreen"),
);
const LearnerSupportAssignmentScreen = lazy(
  () => import("../screens/learner/support-assignments/SupportAssignmentScreen"),
);
const LearnerSupportAssignmentSolveScreen = lazy(
  () => import("../screens/learner/support-assignments/SupportAssignmentSolveScreen"),
);
const LearnerUserProfileScreen = lazy(
  () => import("../screens/learner/profiles/UserProfileScreen"),
);
const LearnerViewUserProfileScreen = lazy(
  () => import("../screens/learner/profiles/ViewUserProfileScreen"),
);
const LearnerUserProfileAchievementScreen = lazy(
  () => import("../screens/learner/profiles/UserProfileAchievementScreen"),
);
const LearnerViewUserProfileAchievementScreen = lazy(
  () => import("../screens/learner/profiles/ViewUserProfileAchievementScreen"),
);
const LearnerSubjectDescriptionChatScreen = lazy(
  () => import("../screens/learner/learning-path/SubjectDescriptionChatScreen"),
);
const LearnerLearningPathScreen = lazy(
  () => import("../screens/learner/learning-path/LearningPathScreen"),
);
const LearnerReadinessScreen = lazy(
  () => import("../screens/learner/learning-path/ReadinessScreen"),
);
const LearnerVocabularyCardsScreen = lazy(
  () => import("../screens/learner/vocabulary/VocabularyCardsScreen"),
);
const LearnerTermCardsScreen = lazy(
  () => import("../screens/learner/term/TermCardsScreen"),
);
const LearnerMarketScreen = lazy(
  () => import("../screens/learner/market/MarketScreen"),
);
const LearnerSupportScreen = lazy(
  () => import("../screens/learner/support/SupportScreen"),
);

// "Fotoğrafı çek çözümü al" used to be the third Pratik yap tab; it is its own page now. A
// bookmarked or shared `/practise?tab=TAKE_PHOTO_GET_ANSWER` lands there instead of silently
// falling back to Pratik's first tab. Decided here, before Pratik mounts, so its practise
// bootstrap never fires for a learner who is only passing through.
const PractiseOrPhotoSolveRedirect = () => {
  const [searchParams] = useSearchParams();
  if (searchParams.get("tab") === "TAKE_PHOTO_GET_ANSWER") {
    return <Navigate to="/photo-solve" replace />;
  }
  return <LearnerPractiseNowScreen />;
};

interface IRouterOption {
  path: string;
  element: JSX.Element;
  errorElement?: JSX.Element;
}

// Back-compat redirects for this feature's three renames (etude → study-group → group-assignment
// → support-assignment). They carry the old route's id params through to the current
// /support-assignments paths, in ONE hop from any old name, so bookmarks and deep links keep
// working. Chaining them would walk a browser through three redirects and break the moment a
// middle hop retires.
const RedirectSupportAssignmentDetail = () => {
  const { supportAssignmentId } = useParams();
  return <Navigate to={`/support-assignments/${supportAssignmentId}`} replace />;
};

// The /practise twin of the above. A past assignment opens under /practise, and that is the URL
// "Önceki ödevlerim" → İncele produced, so it is the one most likely to be sitting in history.
const RedirectPractiseSupportAssignmentDetail = () => {
  const { supportAssignmentId } = useParams();
  return (
    <Navigate
      to={`/practise/support-assignments/${supportAssignmentId}`}
      replace
    />
  );
};

// Old learner URLs, forwarded to where those screens live now. These arrive from notification deep
// links and pasted URLs, which outlive a deploy.
//
// Two traps a plain prefix strip falls into: the assignment family was RENAMED, not de-prefixed
// (/corporate-assignments became /branch-assignments, and stripping alone yields /assignments —
// a path that means something else), and the prefix also appears after /practise and /reports, so
// an ^-anchored replace never reaches it.
const legacyCorporateTarget = (pathname: string): string =>
  pathname
    .replace(/(^|\/)corporate-assignments/, "$1branch-assignments")
    .replace(/(^|\/)corporate-/g, "$1");

const RedirectLegacyCorporate = () => {
  const location = useLocation();
  return (
    <Navigate
      to={legacyCorporateTarget(location.pathname) + location.search + location.hash}
      replace
    />
  );
};

const RedirectSupportAssignmentSolve = () => {
  const { sourceId, activityId } = useParams();
  return (
    <Navigate
      to={`/practise/support-assignments/${sourceId}/activities/${activityId}`}
      replace
    />
  );
};

// Back-compat redirect for the removed /competitions path — contests now live
// at /contests (the duel lobby kept its own /duels route).
const RedirectCompetitionContest = () => {
  const { contestId } = useParams();
  return <Navigate to={`/contests/${contestId}`} replace />;
};

const BaseRouterProvider = () => {
  const { boarding, isFullScreen } = useApp();
  const { userProfile } = useAuth();
  const { pathname } = window.location;

  const retrieveProfileSelectionOptionList = (): IRouterOption[] => {
    return [
      {
        path: "*",
        element: <ProfileSelectionScreen />,
      },
    ];
  };

  const retrieveCommonRouterOptionList = (): IRouterOption[] => {
    return [
      {
        path: "/login/with-external-client",
        element: <LoginWithExternalClientScreen />,
      },
      {
        // Same `/login/with-` prefix on purpose: App.tsx pre-clears the outgoing session for anything under
        // it, which is exactly what spending an impersonation code wants.
        path: "/login/with-impersonation",
        element: <LoginWithImpersonationScreen />,
      },
      {
        path: "/network-error",
        element: <NetworkErrorScreen />,
      },
      {
        path: "/permission-error",
        element: <PermissionErrorScreen />,
      },
      {
        path: "/upload/file-to-application/:base64Data",
        element: <FileToAnyApplicationScreen />,
      },
      {
        path: "/upload/file-to-application/success",
        element: <FileToAnyApplicationSuccessScreen />,
      },
      {
        // The last segment is a single-use QR sign-in code (never a token) — see SolveActivityScreen.
        path: "/solve/activity/:language/:qrLoginCode",
        element: <SolveActivityScreen />,
      },
    ];
  };

  const retrieveLandingRouterOptionList = (): IRouterOption[] => {
    return [
      {
        path: "/",
        element: <LandingScreen />,
      },
      {
        path: "/login",
        element: <LoginScreen />,
      },
      {
        path: "/login/with-k12",
        element: <LoginWithK12Screen />,
      },
      {
        // SEBIT VCloud SSO. Serves both legs: SEBIT sends the learner in with ?token=&clientid=, and the
        // backend's hand-off returns them here with the token pair in the fragment.
        path: "/login/with-sebit",
        element: <LoginWithSebitScreen />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPasswordScreen />,
      },
      {
        path: "*",
        element: <LoginScreen />,
      },
    ];
  };

  const retrieveLearnerDashboardRouterOptionList = (): IRouterOption[] => {
    return [
      {
        path: "/",
        element: <LearnerDashboardScreen />,
        errorElement: <ErrorScreen />,
      },
      {
        path: "/profile",
        element: <LearnerUserProfileScreen />,
      },
      {
        path: "/profiles/:userProfileId/achievements",
        element: <LearnerUserProfileAchievementScreen />,
      },
      {
        path: "/view/profiles/:viewUserProfileId",
        element: <LearnerViewUserProfileScreen />,
      },
      {
        path: "/view/profiles/:viewUserProfileId/achievements",
        element: <LearnerViewUserProfileAchievementScreen />,
      },
      {
        path: "/homeworks",
        element: <LearnerHomeworkScreen />,
      },
      // The Çalışmalar list is retired — destek ödevleri are rows inside Ödevler. A static
      // path outranks the sibling "/support-assignments/:supportAssignmentId", so the detail
      // route below is unaffected.
      {
        path: "/support-assignments",
        element: <Navigate to="/homeworks" replace />,
      },
      {
        path: "/support-assignments/:supportAssignmentId",
        element: <LearnerSupportAssignmentScreen />,
      },
      // Same detail screen under /practise — a past destek ödevi opened from Pratik yap
      // → "Önceki ödevlerim" lives at /practise/support-assignments/:id, mirroring how a past
      // homework re-opens at /practise/branch-assignments/:id. The solve loop already
      // sits under /practise/support-assignments/:sourceId/activities/:activityId.
      {
        path: "/practise/support-assignments/:supportAssignmentId",
        element: <LearnerSupportAssignmentScreen />,
      },
      // Back-compat redirects from every path this feature has been served on: it was renamed
      // etude → study-group → group-assignment → support-assignment, and then folded into Ödevler.
      // A learner's deep link comes from a push notification or a bookmark, both of which outlive
      // all three renames, so each generation redirects in ONE hop; the LIST forms land on
      // /homeworks. Chaining them instead would walk a browser through three redirects and break
      // the moment a middle hop retires.
      {
        path: "/etudes",
        element: <Navigate to="/homeworks" replace />,
      },
      {
        path: "/etudes/:supportAssignmentId",
        element: <RedirectSupportAssignmentDetail />,
      },
      {
        path: "/study-groups",
        element: <Navigate to="/homeworks" replace />,
      },
      {
        path: "/study-groups/:supportAssignmentId",
        element: <RedirectSupportAssignmentDetail />,
      },
      {
        path: "/group-assignments",
        element: <Navigate to="/homeworks" replace />,
      },
      {
        path: "/group-assignments/:supportAssignmentId",
        element: <RedirectSupportAssignmentDetail />,
      },
      {
        // The PRACTISE detail route, not the solve route: this is the 3-segment URL
        // "Önceki ödevlerim" → İncele linked to until this deploy, so it is in live history and in
        // bookmarks. Its non-practise twin and the 5-segment solve path both kept redirects; this
        // one was dropped, and the learner route list has no "*" fallback, so it rendered a blank
        // content pane rather than a 404.
        path: "/practise/group-assignments/:supportAssignmentId",
        element: <RedirectPractiseSupportAssignmentDetail />,
      },
      {
        path: "/practise/study-groups/:supportAssignmentId",
        element: <RedirectPractiseSupportAssignmentDetail />,
      },
      {
        path: "/practise/study-groups/:sourceId/activities/:activityId",
        element: <RedirectSupportAssignmentSolve />,
      },
      {
        path: "/practise/group-assignments/:sourceId/activities/:activityId",
        element: <RedirectSupportAssignmentSolve />,
      },

      // A splat must occupy its own segment in React Router v6 — "/corporate-*" is silently
      // rewritten to "/corporate-/*" and matches nothing. "/x/*" does match bare "/x".
      {
        path: "/corporate-assignments/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/corporate-standard-exam-applications/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/practise/corporate-assignments/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/practise/corporate-standard-exams/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/practise/corporate-standard-exam-applications/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/practise/corporate-handwritten-exams/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/practise/corporate-optical-exams/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/reports/corporate-assignments/*",
        element: <RedirectLegacyCorporate />,
      },
      {
        path: "/branch-assignments/:branchAssignmentId",
        element: <LearnerBranchAssignmentScreen />,
      },
      {
        path: "/standard-exam-applications/:standardExamApplicationId",
        element: <LearnerStandardExamApplicationScreen />,
      },
      {
        path: "/learning-path",
        element: <LearnerLearningPathScreen />,
      },
      {
        // Static "readiness" segment must stay ahead of the :subjectDescriptionId route below
        // (React Router ranks static segments higher regardless, but keep the intent explicit).
        path: "/learning-path/readiness/:subjectDescriptionId",
        element: <LearnerReadinessScreen />,
      },
      {
        // A theme's vocabulary deck as a page (it was a modal over the path).
        // Static segment, so it ranks above /learning-path/:subjectDescriptionId/:contentType.
        path: "/learning-path/vocabulary-cards/:categoryId",
        element: <LearnerVocabularyCardsScreen />,
      },
      {
        // A theme's term deck as a page, the non-language sibling of the
        // vocabulary one. Static segment, so it ranks above
        // /learning-path/:subjectDescriptionId/:contentType.
        path: "/learning-path/term-cards/:categoryId",
        element: <LearnerTermCardsScreen />,
      },
      {
        path: "/learning-path/:subjectDescriptionId/:contentType",
        element: <LearnerSubjectDescriptionChatScreen />,
      },
      {
        path: "/personalized-assignments/:personalizedAssignmentId",
        element: <PersonalizedAssignmentScreen />,
      },
      {
        path: "/practise",
        element: <PractiseOrPhotoSolveRedirect />,
      },
      {
        path: "/photo-solve",
        element: <LearnerPhotoSolveScreen />,
      },
      {
        path: "/photo-solve/:photoSolutionId",
        element: <LearnerPhotoSolutionDetailScreen />,
      },
      {
        path: "/practise/branch-assignments/:branchAssignmentId",
        element: <LearnerPractiseBranchAssignmentByIdScreen />,
      },
      {
        path: "/practise/standard-exams/:standardExamId",
        element: <LearnerPractiseStandardExamByIdScreen />,
      },
      {
        path: "/practise/branch-assignments/:sourceId/activities/:activityId",
        element: <LearnerPractiseApplicationActivityByIdScreen />,
      },
      {
        path: "/practise/personalized-assignments/:sourceId/activities/:activityId",
        element: <LearnerPractiseApplicationActivityByIdScreen />,
      },
      {
        path: "/practise/support-assignments/:sourceId/activities/:activityId",
        element: <LearnerSupportAssignmentSolveScreen />,
      },
      // Back-compat redirects for the older solve paths (etude → study-group → group-assignment
      // → support-assignment). A half-finished solve URL is the one a learner is most likely to
      // have open when a rename lands.
      {
        path: "/practise/etudes/:sourceId/activities/:activityId",
        element: <RedirectSupportAssignmentSolve />,
      },
      {
        path: "/practise/standard-exam-applications/:sourceId/activities/:activityId",
        element: <LearnerPractiseApplicationActivityByIdScreen />,
      },
      {
        path: "/practise/handwritten-exams/:sourceId/activities/:activityId",
        element: <LearnerPractiseApplicationActivityByIdScreen />,
      },
      {
        path: "/practise/optical-exams/:sourceId/activities/:activityId",
        element: <LearnerPractiseApplicationActivityByIdScreen />,
      },
      {
        path: "/reports/branch-assignments/:branchAssignmentReportDetailId",
        element: <LearnerBranchAssignmentReportScreen />,
      },
      {
        path: "/reports/mock-exams/:mockExamId",
        element: <LearnerMockExamReportScreen />,
      },
      {
        path: "/reports",
        element: <LearnerReportListScreen />,
      },
      {
        path: "/framework-annual-plans",
        element: <LearnerFrameworkAnnualPlanListScreen />,
      },
      {
        path: "/announcements",
        element: <LearnerAnnouncementListScreen />,
      },
      {
        path: "/contests",
        element: <LearnerCheckContestScreen />,
      },
      {
        path: "/contests/:contestId",
        element: <LearnerContestScreen />,
      },
      {
        path: "/competitions",
        element: <Navigate to="/contests" replace />,
      },
      {
        path: "/competitions/:contestId",
        element: <RedirectCompetitionContest />,
      },
      {
        path: "/duels",
        element: <LearnerCompetitionScreen duelOnly />,
      },
      {
        path: "/duels/:duelId",
        element: <LearnerDuelGameScreen />,
      },
      {
        path: "/duels/:duelId/spectate",
        element: <LearnerDuelSpectatorScreen />,
      },
      {
        path: "/market",
        element: <LearnerMarketScreen />,
      },
      {
        path: "/support",
        element: <LearnerSupportScreen />,
      },
    ];
  };

  const RenderMainRouterContent = () => {
    if (boarding === "LANDING" || boarding === "DASHBOARD") {
      return (
        <Router>
          <div
            id="container"
            key={`${boarding}-${userProfile?.id}`}
            className="brp-shell"
          >
            <div className="brp-row">
              {isFullScreen ? null : (
                <div className="brp-nav">
                  {pathname.startsWith("/upload") ? null : (
                    <MainLeftNavigation />
                  )}
                </div>
              )}
              <div className="brp-content">
                <div
                  className={`brp-pane ${isFullScreen ? "" : "brp-pane-pad"}`}
                >
                  <div className="brp-scroll no-scrollbar">
                    <DraggableMascot xAxisOnly={true} />
                    <div
                      className={`brp-fill ${
                        userProfile && !isFullScreen ? "brp-container" : ""
                      }`}
                    >
                      <Suspense
                        fallback={
                          <div className="brp-center">
                            <BaseLoading
                              size="large"
                              variant="wordmark"
                              primaryColor="#57C2D3"
                              secondaryColor="#57C2D3"
                            />
                          </div>
                        }
                      >
                        <Routes>
                          {boarding === "LANDING"
                            ? [
                                ...commonRouterOptionList,
                                ...landingRouterOptionList,
                              ].map((routerOption, index) => (
                                <Route
                                  key={index}
                                  path={routerOption.path}
                                  errorElement={routerOption.errorElement}
                                  element={routerOption.element}
                                />
                              ))
                            : userProfile
                              ? userProfile.role === "LEARNER"
                                ? [
                                    ...commonRouterOptionList,
                                    ...learnerDashboardRouterOptionList,
                                  ].map((routerOption, index) => (
                                    <Route
                                      key={index}
                                      path={routerOption.path}
                                      errorElement={
                                        routerOption.errorElement
                                          ? routerOption.errorElement
                                          : null
                                      }
                                      element={routerOption.element}
                                    />
                                  ))
                                : null
                              : null}
                        </Routes>
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {boarding === "DASHBOARD" && userProfile?.role === "LEARNER" ? (
            <DuelSocialLayer />
          ) : null}
          {/* Not learner-gated: every profile on this app holds a websocket, so every profile can be
              reaped for inactivity. */}
          {boarding === "DASHBOARD" ? <SessionInactiveModal /> : null}
        </Router>
      );
    } else if (boarding === "NETWORK_ERROR") {
      return (
        <div className="brp-center">
          <NetworkErrorScreen />;
        </div>
      );
    } else if (boarding === "PROFILE_SELECTION_REQUIRED") {
      return (
        <Router>
          <div className="brp-scroll no-scrollbar">
            <div className="brp-fill">
              <Routes>
                <Route>
                  {profileSelectionOptionList.map((routerOption, index) => (
                    <Route
                      key={index}
                      path={routerOption.path}
                      errorElement={routerOption.errorElement}
                      element={routerOption.element}
                    />
                  ))}
                </Route>
              </Routes>
            </div>
          </div>
        </Router>
      );
    } else if (boarding === "ACCOUNT_ACTIVATION_REQUIRED") {
      return (
        <div className="brp-center">
          <AccountActivationErrorScreen />
        </div>
      );
    } else if (boarding === "PROFILE_INACTIVE") {
      // Pasif öğrenci: the server refuses every token for this learner profile.
      return (
        <div className="brp-center">
          <ProfileInactiveErrorScreen />
        </div>
      );
    } else if (boarding === "TOKEN_ERROR") {
      return (
        <div className="brp-center">
          <TokenErrorScreen />
        </div>
      );
    } else if (boarding === "SERVER_UNREACHABLE") {
      return (
        <div className="brp-center">
          <ServerUnreachableErrorScreen />
        </div>
      );
    } else {
      return (
        <div className="brp-center">
          <BaseLoading
            size="large"
            variant="wordmark"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        </div>
      );
    }
  };

  const profileSelectionOptionList = retrieveProfileSelectionOptionList();
  const commonRouterOptionList = retrieveCommonRouterOptionList();
  const landingRouterOptionList = retrieveLandingRouterOptionList();
  const learnerDashboardRouterOptionList =
    retrieveLearnerDashboardRouterOptionList();

  return RenderMainRouterContent();
};

export default BaseRouterProvider;
